import React from "react";
import ReactDOM from "react-dom";
const D = window.SiteData;
/* =========================================================
   珂玺资本 — Portfolio + IPO Cases pages
   ========================================================= */

const { useState: useStateP, useEffect: useEffectP } = React;

// 阶段固定文案（全部/已上市/Pre-IPO/早期组合）——统一来源，含中英，不进入页面文案右键编辑。
// tab 与下方按阶段分组的标题都从这里取值。
const STAGE_LABELS = {
  all:      { cn: "全部",     en: "All" },
  listed:   { cn: "已上市",   en: "Listed" },
  pipeline: { cn: "Pre-IPO",  en: "Pre-IPO" },
  early:    { cn: "早期组合", en: "Early-stage" },
};

// 赛道固定文案：API 返回英文 code，前台按语言映射为中/英标签。
const SECTOR_LABELS = {
  semiconductor: { cn: "半导体",       en: "Semiconductors" },
  "ai-driving":  { cn: "AI / 智驾",    en: "AI / Driving" },
  biotech:       { cn: "生物医疗",     en: "Life Sciences" },
  manufacturing: { cn: "智造 / 航天",  en: "Smart Mfg / Aerospace" },
};
const sectorLabel = (code, lang) => {
  const m = SECTOR_LABELS[code];
  return m ? (lang === "cn" ? m.cn : m.en) : (code || "");
};

// 把 API row 映射到组件期望的 shape（兼容历史 data.js 字段）
function mapApiRow(r) {
  const fmtIpo = (d) => {
    if (!d) return "";
    const [y, m] = String(d).split("-");
    return m ? `${y}.${m}` : y;
  };
  const retNum = r.return_multiple == null ? null : Number(r.return_multiple);
  const retLabelByStage = {
    listed:   { cn: "倍收益完美退出",  en: "exit multiple" },
    pipeline: { cn: "Pre-IPO",         en: "Pre-IPO" },
    early:    { cn: "持有",            en: "Hold" },
  };
  const exitLabelOverride = {
    locked:   { cn: "锁定期",    en: "Locked" },
    hold:     { cn: "持有中",    en: "Holding" },
    exited:   { cn: "已退出",    en: "Exited" },
  };
  return {
    slug: r.slug,
    cn: r.name_zh,
    en: r.name_en,
    ticker: r.ticker || "",
    market: r.market || "",
    marketEn: r.market_en || "",
    ipoYear: fmtIpo(r.ipo_date) || fmtIpo(r.kexi_invest_date) || "",
    year: r.kexi_invest_date ? String(r.kexi_invest_date).slice(0, 4) : "",
    sector: r.sector,
    stage: r.stage,
    desc: { cn: r.summary_zh, en: r.summary_en },
    story: { cn: r.body_zh_md || r.summary_zh, en: r.body_en_md || r.summary_en },
    glyph: r.name_zh,
    logoKey: r.logo_key,
    ret: retNum != null ? String(retNum % 1 === 0 ? retNum.toFixed(0) : retNum) : "—",
    retLabel: retNum != null
      ? retLabelByStage[r.stage] || { cn: "倍", en: "×" }
      : (exitLabelOverride[r.exit_status] || { cn: "持有中", en: "Holding" }),
    status: { cn: r.summary_zh, en: r.summary_en },
  };
}

// Hook：fetch + bucket 成 { listed, pipeline, early }
function useProjects() {
  const [state, setState] = useStateP({ loading: true, listed: [], pipeline: [], early: [], error: null });
  useEffectP(() => {
    let cancelled = false;
    fetch("/api/projects")
      .then(r => r.ok ? r.json() : Promise.reject(new Error("fetch failed")))
      .then(rows => {
        if (cancelled) return;
        const mapped = rows.map(mapApiRow);
        setState({
          loading: false,
          error: null,
          listed:   mapped.filter(p => p.stage === "listed"),
          pipeline: mapped.filter(p => p.stage === "pipeline"),
          early:    mapped.filter(p => p.stage === "early"),
        });
      })
      .catch(err => { if (!cancelled) setState(s => ({ ...s, loading: false, error: err })); });
    return () => { cancelled = true; };
  }, []);
  return state;
}

// Logo 墙单元：有可用 logo → logo 居中、公司名落左下角；无 logo（含图片加载失败）→ 公司名居中。
// 右下角始终显示股票代码或 Pre-IPO。
function LogoCell({ c, lang, onClick }) {
  const [imgOk, setImgOk] = useStateP(true);
  const logo = fileUrlS(c.logoKey);
  const showLogo = !!logo && imgOk;
  const name = lang === "cn" ? c.cn : c.en;
  const ticker = c.ticker || "Pre-IPO";
  return (
    <div className={`logo-cell ${showLogo ? "logo-cell-logo" : ""}`} onClick={onClick} style={{ cursor: "pointer" }}>
      {showLogo ? (
        <>
          <div className="logo-mark"><img src={logo} alt={name} onError={() => setImgOk(false)} /></div>
          <div className="logo-name">{name}</div>
        </>
      ) : (
        <div className="glyph">{name}</div>
      )}
      <div className="meta-r">{ticker}</div>
    </div>
  );
}

// =========================================================
// PORTFOLIO — full list with tabs / filter
// =========================================================
function PortfolioPage({ lang, setRoute, nested }) {
  const t = (keyOrCn, en) => {
    if (typeof keyOrCn === "string" && keyOrCn.indexOf(".") > 0 && window.I18N) {
      const [ns, k] = keyOrCn.split(".", 2);
      const val = window.I18N[lang] && window.I18N[lang][ns] && window.I18N[lang][ns][k];
      if (val !== undefined) return val;
    }
    // fallback: 兼容旧 t(cn, en) 调用
    return lang === "cn" ? keyOrCn : (en !== undefined ? en : keyOrCn);
  };
  const [tab, setTab] = useStateP("all");
  const { listed, pipeline, early, loading } = useProjects();

  const tabs = [
    { id: "all", ...STAGE_LABELS.all, count: listed.length + pipeline.length + early.length },
    { id: "listed", ...STAGE_LABELS.listed, count: listed.length },
    { id: "pipeline", ...STAGE_LABELS.pipeline, count: pipeline.length },
    { id: "early", ...STAGE_LABELS.early, count: early.length },
  ];
  const stageLabel = (k) => (lang === "cn" ? STAGE_LABELS[k].cn : STAGE_LABELS[k].en);

  return (
    <div className={nested ? "" : "page"} data-screen-label={nested ? undefined : "04 Portfolio"}>
      {!nested && <InvestTabs route="portfolio" setRoute={setRoute} lang={lang} />}
      {!nested && (
      <section style={{ paddingTop: 60, paddingBottom: 60 }}>
        <div className="container-wide">
          <div className="eyebrow" style={{ marginBottom: 24 }}>{t('portfolio.t001')}</div>
          <h1 className="h-display">
            {t('portfolio.t002')} {lang === "cn"
              ? <span style={{ color: "var(--gold)" }}>每一家</span>
              : <span className="serif-en" style={{ color: "var(--gold)" }}>backed.</span>}
          </h1>
          <p className="body-lg body-mute" style={{ maxWidth: 720, marginTop: 32 }}>
            {t('portfolio.t003')}
          </p>
        </div>
      </section>
      )}

      <section style={{ paddingBottom: 80 }}>
        <div className="container-wide">
          {/* tab 标签与计数均为固定/统计值，不进入页面文案编辑 */}
          <div className="tabs" data-cms-skip>
            {tabs.map(tb => (
              <button key={tb.id} className={`tab ${tab === tb.id ? "active" : ""}`} onClick={() => setTab(tb.id)}>
                {lang === "cn" ? tb.cn : tb.en}<span className="count">{tb.count}</span>
              </button>
            ))}
          </div>

          {(tab === "all" || tab === "listed") && (
            <>
              <div className="eyebrow-mute" data-cms-skip style={{ margin: "32px 0 16px" }}>· {stageLabel("listed")}</div>
              {loading
                ? <div className="body-mute" style={{ padding: "40px 0" }}>{lang === "cn" ? "加载中…" : "Loading…"}</div>
                : <ProjectRows lang={lang} items={listed} />}
            </>
          )}

          {(tab === "all" || tab === "pipeline") && (
            <>
              <div className="eyebrow-mute" data-cms-skip style={{ margin: tab === "all" ? "60px 0 16px" : "32px 0 16px" }}>· {stageLabel("pipeline")}</div>
              {loading
                ? <div className="body-mute" style={{ padding: "40px 0" }}>{lang === "cn" ? "加载中…" : "Loading…"}</div>
                : <ProjectRows lang={lang} items={pipeline} />}
            </>
          )}

          {(tab === "all" || tab === "early") && (
            <>
              <div className="eyebrow-mute" data-cms-skip style={{ margin: tab === "all" ? "60px 0 16px" : "32px 0 16px" }}>· {stageLabel("early")}</div>
              {loading
                ? <div className="body-mute" style={{ padding: "40px 0" }}>{lang === "cn" ? "加载中…" : "Loading…"}</div>
                : <ProjectRows lang={lang} items={early} />}
            </>
          )}

        </div>
      </section>

      {/* Logo wall */}
      <section className="section" style={{ background: "var(--bg-0)" }}>
        <div className="container-wide">
          <SectionHead
            lang={lang}
            num="—"
            kicker={<span data-cpath-fixed="portfolio.kLogoWall">{t('portfolio.kLogoWall')}</span>}
            title={t('portfolio.t007')}
            lead={t('portfolio.t008')}
          />
          {/* 公司网格：已上市 + Pre-IPO，均来自「投资项目」菜单。整块右键 → 跳转投资项目维护。 */}
          <div className="logo-wall" data-cms-redirect="projects">
            {listed.concat(pipeline).map((c) => (
              <LogoCell key={c.slug} c={c} lang={lang} onClick={() => setRoute(`portfolio/${c.slug}`)} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// =========================================================
// IPO CASES — feature page with hover expand
// =========================================================
function CasesPage({ lang, setRoute }) {
  const t = (keyOrCn, en) => {
    if (typeof keyOrCn === "string" && keyOrCn.indexOf(".") > 0 && window.I18N) {
      const [ns, k] = keyOrCn.split(".", 2);
      const val = window.I18N[lang] && window.I18N[lang][ns] && window.I18N[lang][ns][k];
      if (val !== undefined) return val;
    }
    // fallback: 兼容旧 t(cn, en) 调用
    return lang === "cn" ? keyOrCn : (en !== undefined ? en : keyOrCn);
  };
  const { listed, pipeline, loading } = useProjects();
  return (
    <div className="page" data-screen-label="05 IPO Cases">
      <InvestTabs route="cases" setRoute={setRoute} lang={lang} />

      {/* Hero */}
      <section style={{ paddingTop: 60, paddingBottom: 80, position: "relative", overflow: "hidden" }}>
        <div style={{
          position: "absolute", right: "-120px", top: "10%",
          fontFamily: "var(--f-serif-en)",
          fontStyle: "italic",
          fontSize: 380,
          color: "transparent",
          WebkitTextStroke: "1px rgba(201,168,107,0.1)",
          pointerEvents: "none",
        }}>IPO</div>
        <div className="container-wide" style={{ position: "relative" }}>
          <div className="eyebrow" style={{ marginBottom: 24 }}>{t('portfolio.t009')}</div>
          <h1 className="h-display">{t('portfolio.t010')}</h1>
          <h1 className="h-display">
            {lang === "cn"
              ? <span data-cpath-fixed="portfolio.casesReturn" style={{ color: "var(--gold)" }}>{t('portfolio.casesReturn')}</span>
              : <span data-cpath-fixed="portfolio.casesReturn" className="serif-en" style={{ color: "var(--gold)" }}>{t('portfolio.casesReturn')}</span>}
          </h1>
          <p className="body-lg body-mute" style={{ maxWidth: 720, marginTop: 36 }}>
            {t('portfolio.t011')}
          </p>
        </div>
      </section>

      {/* Quick stats */}
      <section style={{ paddingBottom: 80 }}>
        <div className="container-wide">
          <div className="stat-grid">
            {[
              { numKey: "casesStat1", suffix: "", label: t('portfolio.t012'), idx: "01" },
              { numKey: "casesStat2", suffix: "", label: t('portfolio.t013'), idx: "02" },
              { numKey: "casesStat3", suffix: "", label: t('portfolio.t014'), idx: "03" },
              { numKey: "casesStat4", suffix: "×", label: t('portfolio.t015'), idx: "04" },
            ].map((s, i) => (
              <div className="stat-cell" key={i}>
                <div className="index">{s.idx}</div>
                <div className="stat-num">
                  <Counter to={t('portfolio.' + s.numKey)} suffix={s.suffix}
                    numFixed={`portfolio.${s.numKey}`} bilingual />
                </div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case rows */}
      <section className="section-tight">
        <div className="container-wide">
          <SectionHead
            lang={lang}
            num={t('portfolio.num01')}
            kicker={<span data-cpath-fixed="portfolio.kCaseByCase">{t('portfolio.kCaseByCase')}</span>}
            title={t('portfolio.t016')}
            lead={t('portfolio.t017')}
          />
          {loading
            ? <div className="body-mute" style={{ padding: "40px 0" }}>{lang === "cn" ? "加载中…" : "Loading…"}</div>
            : <ListedCaseRows lang={lang} listed={listed} setRoute={setRoute} expandable />}
        </div>
      </section>

      {/* Logo wall */}
      <section className="section">
        <div className="container-wide">
          <SectionHead
            lang={lang}
            num={t('portfolio.num02')}
            kicker={<span data-cpath-fixed="portfolio.kThePipeline">{t('portfolio.kThePipeline')}</span>}
            title={<span data-cpath-fixed="portfolio.casesWallTitle">{t('portfolio.casesWallTitle')}</span>}
          />
          {/* Pre-IPO 储备墙：列 pipeline 公司；整块右键跳转投资项目维护 */}
          <div className="logo-wall" data-cms-redirect="projects">
            {pipeline.map((c) => (
              <LogoCell key={c.slug} c={c} lang={lang} onClick={() => setRoute(`portfolio/${c.slug}`)} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// ----- Project rows (统一三种 stage 的卡片+展开 UI) -----
// 列表项 c 来自 useProjects()，已映射；展开时按需 fetch /api/projects/:slug 拿 body_md/gallery/timeline
function ProjectRows({ lang, items }) {
  const [openSlug, setOpenSlug] = useStateP(null);
  if (!items || items.length === 0) return null;
  return (
    <div>
      {items.map((c) => (
        <ProjectRow
          key={c.slug}
          c={c}
          lang={lang}
          open={openSlug === c.slug}
          onToggle={() => setOpenSlug(openSlug === c.slug ? null : c.slug)}
        />
      ))}
    </div>
  );
}

// 单行 + 详情 lazy fetch
function ProjectRow({ c, lang, open, onToggle }) {
  const isZh = lang === "cn";
  const stageHeadLabel = c.stage === "listed"
    ? c.ipoYear
    : (c.year ? (isZh ? `投资 ${c.year}` : `Invest ${c.year}`) : "");
  const showRet = c.ret !== "—";

  return (
    <React.Fragment>
      <div
        className={`case-card ${open ? "expanded" : ""}`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        data-cms-redirect="projects"
        data-cms-redirect-id={c.slug}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
      >
        <div className="yr">{stageHeadLabel}</div>
        <div>
          <div className="name">{isZh ? c.cn : c.en}</div>
          <div className="name-en">
            {isZh ? c.en : c.cn}
            {c.ticker ? ` · ${c.ticker}` : ""}
          </div>
          <div className="desc">{isZh ? c.desc.cn : c.desc.en}</div>
        </div>
        <div className="sector">{sectorLabel(c.sector, lang)}</div>
        <div>
          {showRet ? (
            <div className="ret">{c.ret}<span className="unit">{isZh ? c.retLabel.cn : c.retLabel.en}</span></div>
          ) : (
            <div style={{ color: "var(--gold)", fontFamily: "var(--f-serif-cn)", fontSize: 18 }}>
              {isZh ? c.retLabel.cn : c.retLabel.en}
            </div>
          )}
          {(c.market || c.marketEn) && (
            <div className="mono" style={{ color: "var(--mute-2)", marginTop: 6, fontSize: 11, letterSpacing: "0.08em" }}>
              {isZh ? c.market : c.marketEn}
            </div>
          )}
        </div>
        <div className="arr-wrap" aria-label={open ? "collapse" : "expand"}>
          {/* 向下/向上箭头：收起时↓，展开时↑ */}
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d={open ? "M3 9l4-4 4 4" : "M3 5l4 4 4-4"} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
      {open && <ProjectDetailBlock slug={c.slug} lang={lang} fallback={c} />}
    </React.Fragment>
  );
}

// 展开区：lazy fetch detail，渲染 body / gallery / timeline
function ProjectDetailBlock({ slug, lang, fallback }) {
  const [state, setState] = useStateP({ loading: true, data: null, error: null });
  useEffectP(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    fetch(`/api/projects/${encodeURIComponent(slug)}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => { if (!cancelled) setState({ loading: false, data: d, error: null }); })
      .catch(err => { if (!cancelled) setState({ loading: false, data: null, error: err }); });
    return () => { cancelled = true; };
  }, [slug]);

  const { loading, data, error } = state;
  const isZh = lang === "cn";

  // fallback 字段，加载失败时尽量保留 list 卡片摘要
  const body = data
    ? (isZh ? data.body_zh_md : data.body_en_md) || (isZh ? data.summary_zh : data.summary_en)
    : (isZh ? fallback.desc.cn : fallback.desc.en);
  const gallery = Array.isArray(data?.gallery_keys) ? data.gallery_keys : [];
  const timeline = Array.isArray(data?.timeline) ? data.timeline : [];

  return (
    <div className="case-detail open">
      <div className="case-detail-inner">
        {loading && <div className="body-mute">{isZh ? "加载中…" : "Loading…"}</div>}
        {error && <div className="body-mute" style={{ color: "var(--danger, #c64)" }}>{isZh ? "加载失败" : "Load failed"}</div>}
        {!loading && !error && (
          <>
            {/* 公司介绍 */}
            <div className="detail-block">
              <div className="eyebrow-mute">{isZh ? "公司介绍" : "About"}</div>
              <div className="detail-body">{body}</div>
            </div>

            {/* 图片画廊 */}
            {gallery.length > 0 && (
              <div className="detail-block">
                <div className="eyebrow-mute">{isZh ? "图片" : "Gallery"}</div>
                <ProjectGallery images={gallery} lang={lang} />
              </div>
            )}

            {/* 时间线 */}
            {timeline.length > 0 && (
              <div className="detail-block">
                <div className="eyebrow-mute">{isZh ? "时间线" : "Timeline"}</div>
                <div className="timeline">
                  {timeline.map((t, i) => (
                    <div className="timeline-row" key={i}>
                      <div className="timeline-year">{t.year}</div>
                      <div className="timeline-event">
                        {isZh ? (t.event_zh || t.event_en) : (t.event_en || t.event_zh)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// 图片画廊（轻量 lightbox）
// 全屏放大查看（与动态文章详情页一致：适配窗口 ↔ 原图切换 + 关闭按钮 + ESC + 锁滚动）
function ImageLightbox({ url, onClose, lang }) {
  const isZh = lang !== "en";
  const [actual, setActual] = useStateP(false);
  useEffectP(() => {
    if (!url) return;
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [url]);
  if (!url) return null;
  return ReactDOM.createPortal(
    // 渲染到 body：脱离 .page 的 transform，position:fixed 才相对视口
    <div className={`img-lightbox ${actual ? "is-actual" : "is-fit"}`} onClick={onClose}>
      <button className="img-lightbox-close" aria-label="关闭" onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>
      <img src={url} alt=""
        title={actual ? (isZh ? "点击恢复适配窗口" : "Click to fit") : (isZh ? "点击查看原图" : "Click for actual size")}
        onClick={(e) => { e.stopPropagation(); setActual((v) => !v); }} />
    </div>,
    document.body
  );
}

function ProjectGallery({ images, lang, onZoom, full }) {
  const [preview, setPreview] = useStateP(null);
  const open = (src) => { if (onZoom) onZoom(fileUrlS(src)); else setPreview(fileUrlS(src)); };
  return (
    <>
      <div className={`gallery${full ? " gallery-full" : ""}`}>
        {images.map((src, i) => (
          <button
            key={i}
            className="gallery-cell"
            onClick={(e) => { e.stopPropagation(); open(src); }}
            aria-label="preview"
          >
            <img src={fileUrlS(src)} alt="" loading="lazy" />
          </button>
        ))}
      </div>
      {/* 无外部 onZoom 时（如列表展开视图）自带 lightbox */}
      {!onZoom && <ImageLightbox url={preview} lang={lang} onClose={() => setPreview(null)} />}
    </>
  );
}

// ----- Listed case rows: 兼容老调用点，转为 ProjectRows -----
function ListedCaseRows({ lang, listed }) {
  return <ProjectRows lang={lang} items={listed} />;
}

// =========================================================
// PROJECT DETAIL — single portfolio company page
// =========================================================
function ProjectDetailPage({ lang, slug, setRoute }) {
  const [state, setState] = useStateP({ loading: true, data: null, error: null });
  const [zoom, setZoom] = useStateP(null);   // 主图 / 画廊 共用的放大查看
  useEffectP(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    fetch(`/api/projects/${encodeURIComponent(slug)}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status === 404 ? "not_found" : "fetch_failed"))
      .then(d => { if (!cancelled) setState({ loading: false, data: d, error: null }); })
      .catch(err => { if (!cancelled) setState({ loading: false, data: null, error: err }); });
    return () => { cancelled = true; };
  }, [slug]);

  const { loading, data, error } = state;
  const isZh = lang === "cn";

  if (loading) {
    return (
      <div className="page" data-screen-label="06 Project">
        <div className="container-wide" style={{ paddingTop: 80, paddingBottom: 80 }}>
          <div className="body-mute">{isZh ? "加载中…" : "Loading…"}</div>
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="page" data-screen-label="06 Project">
        <div className="container-wide" style={{ paddingTop: 80, paddingBottom: 80 }}>
          <div className="eyebrow">{isZh ? "未找到项目" : "Project not found"}</div>
          <button className="btn-link" style={{ marginTop: 24 }} onClick={() => setRoute("portfolio")}>
            ← {isZh ? "返回投资组合" : "Back to portfolio"}
          </button>
        </div>
      </div>
    );
  }

  const name = isZh ? data.name_zh : data.name_en;
  const subname = isZh ? data.name_en : data.name_zh;
  const summary = isZh ? data.summary_zh : data.summary_en;
  const body = isZh ? data.body_zh_md : data.body_en_md;
  const market = isZh ? data.market : data.market_en;
  const stageLabel = {
    listed:   { cn: "已上市",  en: "Listed" },
    pipeline: { cn: "Pre-IPO", en: "Pre-IPO" },
    early:    { cn: "早期",    en: "Early-stage" },
  }[data.stage] || { cn: data.stage, en: data.stage };

  return (
    <div className="page" data-screen-label="06 Project" data-cms-redirect="projects" data-cms-redirect-id={data.slug}>
      <section style={{ paddingTop: 120, paddingBottom: 40 }}>
        <div className="container-wide">
          {/* 阶段·代码·市场 一行，返回按钮在同一行最右、垂直居中对齐 */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24 }}>
            <div className="eyebrow">
              {isZh ? stageLabel.cn : stageLabel.en}
              {data.ticker ? <span style={{ marginLeft: 12, color: "var(--gold)" }}> · {data.ticker}</span> : null}
              {market ? <span style={{ marginLeft: 8, color: "var(--mute-2)" }}> · {market}</span> : null}
            </div>
            <button
              onClick={() => setRoute("portfolio")}
              style={{ background: "none", border: "none", color: "var(--mute)", fontSize: 13, cursor: "pointer", padding: 0, whiteSpace: "nowrap" }}
            >
              ← {isZh ? "返回投资组合" : "Back to portfolio"}
            </button>
          </div>
          <h1 className="h-display" style={{ marginTop: 16 }}>{name}</h1>
          <div className="mono" style={{ color: "var(--mute-2)", marginTop: 8, letterSpacing: "0.08em" }}>{subname}</div>
          <p className="body-lg body-mute" style={{ maxWidth: 720, marginTop: 32 }}>{summary}</p>
        </div>
      </section>

      {data.cover_key && (
        <section style={{ paddingBottom: 60 }}>
          <div className="container-wide">
            <img className="project-cover" src={fileUrlS(data.cover_key)} alt={name}
              onClick={() => setZoom(fileUrlS(data.cover_key))}
              style={{ display: "block", maxWidth: "100%", height: "auto", margin: "0 auto", cursor: "zoom-in" }} />
          </div>
        </section>
      )}

      {body && (
        <section style={{ paddingBottom: 60 }}>
          <div className="container-wide">
            <div className="eyebrow-mute">{isZh ? "公司介绍" : "Company"}</div>
            <div style={{ marginTop: 16, color: "var(--ivory-2)", fontSize: 15, lineHeight: 1.9, maxWidth: 820, whiteSpace: "pre-wrap" }}>
              {body}
            </div>
          </div>
        </section>
      )}

      {Array.isArray(data.gallery_keys) && data.gallery_keys.length > 0 && (
        <section style={{ paddingBottom: 60 }}>
          <div className="container-wide">
            <div className="eyebrow-mute">{isZh ? "图片" : "Gallery"}</div>
            <div style={{ marginTop: 16 }}>
              <ProjectGallery images={data.gallery_keys} lang={isZh ? "cn" : "en"} onZoom={setZoom} full />
            </div>
          </div>
        </section>
      )}

      {Array.isArray(data.timeline) && data.timeline.length > 0 && (
        <section className="section">
          <div className="container-wide">
            <div className="eyebrow">{isZh ? "时间线" : "Timeline"}</div>
            <div style={{ marginTop: 32, display: "grid", gap: 0, borderTop: "1px solid var(--line)" }}>
              {data.timeline.map((t, i) => (
                <div key={i} style={{
                  padding: "20px 0",
                  borderBottom: "1px solid var(--line)",
                  display: "grid",
                  gridTemplateColumns: "120px 1fr",
                  gap: 24,
                  alignItems: "baseline",
                }}>
                  <div className="mono" style={{ color: "var(--gold)", fontSize: 14, letterSpacing: "0.08em" }}>{t.year}</div>
                  <div style={{ color: "var(--ivory-2)", fontSize: 15, lineHeight: 1.7 }}>
                    {isZh ? (t.event_zh || t.event_en) : (t.event_en || t.event_zh)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.return_multiple != null && (
        <section style={{ padding: "60px 0" }}>
          <div className="container-wide" style={{ display: "flex", gap: 48, alignItems: "baseline" }}>
            <div className="eyebrow">{isZh ? "投资收益" : "Return"}</div>
            <div style={{ fontFamily: "var(--f-serif-cn)", fontSize: 56, color: "var(--gold)" }}>
              {Number(data.return_multiple) % 1 === 0
                ? Number(data.return_multiple).toFixed(0)
                : data.return_multiple}
              <span style={{ fontSize: 18, marginLeft: 8, color: "var(--mute)" }}>{isZh ? "倍" : "×"}</span>
            </div>
          </div>
        </section>
      )}

      <ImageLightbox url={zoom} lang={lang} onClose={() => setZoom(null)} />
    </div>
  );
}

Object.assign(window, { PortfolioPage, CasesPage, ListedCaseRows, ProjectDetailPage });
